import { useMemo } from 'react';
import { Button, IconTile, Popover, Tag } from '@humanforce/mango-ui-react';
import type { PayRunApprovalDto, PayRunDetailDto } from './payRunsApiTypes';
import type { PayRunExceptionsPage } from './payRunPopulationApiTypes';
import type { PayRunBankingInput } from './payRunBankingModel';
import {
  buildPayRunTimeline,
  type PayRunTimelineAction,
  type PayRunTimelineActionVerb,
  type PayRunTimelineStep,
  type PayRunTimelineStepState,
} from './payRunTimelineModel';
import { PayRunProgressList } from './PayRunProgressList';
import { PopoverGroupProvider, useExclusivePopover } from './usePopoverGroup';
import './PayRunTimeline.css';

interface PayRunTimelineProps {
  payRun: PayRunDetailDto;
  approval: PayRunApprovalDto | null;
  exceptions: PayRunExceptionsPage | null;
  busy?: boolean;
  /** Optional Banking (BSB) evidence for the Review stage. Omitted callers keep the pre-PRR-315 behaviour. */
  banking?: PayRunBankingInput;
  onAction: (verb: PayRunTimelineActionVerb) => void;
  showOperatingState?: boolean;
}

type TileColor = 'green' | 'blue' | 'yellow' | 'red' | 'grey';
type IconName = 'status-positive' | 'status-no-action' | 'clock' | 'status-caution' | 'status-critical' | 'status-pending';

const STEP_BADGE: Record<PayRunTimelineStepState, { color: TileColor; icon: IconName }> = {
  complete: { color: 'green', icon: 'status-positive' },
  current: { color: 'blue', icon: 'status-no-action' },
  in_progress: { color: 'blue', icon: 'clock' },
  warning: { color: 'yellow', icon: 'status-caution' },
  rejected: { color: 'red', icon: 'status-critical' },
  upcoming: { color: 'grey', icon: 'status-no-action' },
  cancelled: { color: 'grey', icon: 'status-no-action' },
};

const STATE_DESCRIPTION: Record<PayRunTimelineStepState, string> = {
  complete: 'completed',
  current: 'current step',
  in_progress: 'in progress',
  warning: 'needs attention',
  rejected: 'rejected',
  upcoming: 'not started',
  cancelled: 'cancelled',
};

const StepMenu = ({
  step,
  busy,
  onAction,
}: {
  step: PayRunTimelineStep;
  busy: boolean;
  onAction: (action: PayRunTimelineAction) => void;
}) => (
  <PayRunProgressList
    testId={`pay-run-timeline-panel-${step.id}`}
    idPrefix={`pay-run-timeline-menu-${step.id}`}
    label={step.menuLabel ?? ''}
    items={step.subSteps}
    footer={step.action && (
      <Button
        label={step.action.label}
        size="medium"
        fullWidth
        loading={busy}
        disabled={busy}
        onClick={() => onAction(step.action as PayRunTimelineAction)}
      />
    )}
  />
);

const StepContent = ({ step }: { step: PayRunTimelineStep }) => {
  const badge = STEP_BADGE[step.state];
  return (
    <>
      <IconTile icon={badge.icon} color={badge.color} />
      <span className="pay-run-timeline__labels">
        <span className="pay-run-timeline__title mgo-text-xs mgo-weight-strong">{step.title}</span>
        <span className="pay-run-timeline__detail mgo-text-xs mgo-color-muted">{step.detail}</span>
      </span>
    </>
  );
};

const TimelineStep = ({
  step,
  isCurrent,
  isLast,
  busy,
  onAction,
}: {
  step: PayRunTimelineStep;
  isCurrent: boolean;
  isLast: boolean;
  busy: boolean;
  onAction: (action: PayRunTimelineAction) => void;
}) => {
  const popover = useExclusivePopover(step.id);
  const interactive = step.subSteps.length > 0
    && step.state !== 'upcoming'
    && step.state !== 'cancelled';
  const label = `${step.title}: ${step.detail} — ${STATE_DESCRIPTION[step.state]}`;
  const stepClassName = `pay-run-timeline__step pay-run-timeline__step--${step.state}`;

  return (
    <li className="pay-run-timeline__item" aria-current={isCurrent ? 'step' : undefined}>
      {interactive ? (
        <Popover
          ref={popover.ref}
          onShow={popover.onShow}
          onHide={popover.onHide}
          className="pay-run-progress-popover"
          testId={`pay-run-timeline-menu-${step.id}`}
          trigger={(
            <button
              type="button"
              className={stepClassName}
              aria-label={label}
              aria-expanded={popover.isOpen}
              data-testid={`pay-run-timeline-step-${step.id}`}
            >
              <StepContent step={step} />
            </button>
          )}
        >
          <StepMenu step={step} busy={busy} onAction={onAction} />
        </Popover>
      ) : (
        <span
          className={`${stepClassName} pay-run-timeline__step--static`}
          aria-label={label}
          role="img"
          data-testid={`pay-run-timeline-step-${step.id}`}
        >
          <StepContent step={step} />
        </span>
      )}
      {!isLast && (
        <span
          aria-hidden="true"
          className={`pay-run-timeline__connector${step.state === 'complete' ? ' pay-run-timeline__connector--complete' : ''}`}
        />
      )}
    </li>
  );
};

export const PayRunTimeline = ({
  payRun,
  approval,
  exceptions,
  busy = false,
  banking,
  onAction,
  showOperatingState = true,
}: PayRunTimelineProps) => {
  const timeline = useMemo(
    () => buildPayRunTimeline({ payRun, approval, exceptions, banking }),
    [payRun, approval, exceptions, banking],
  );

  return (
    <section className="mgo-stack mgo-gap-s" aria-labelledby="pay-run-timeline-heading">
      <div className="mgo-cluster mgo-gap-s" data-testid="pay-run-timeline-operating-state">
        <span id="pay-run-timeline-heading" className="sr-only">Pay run progress</span>
        {showOperatingState && (
          <Tag
            label={timeline.operatingStateLabel}
            tone={timeline.operatingStateTone === 'green' ? 'positive' : 'neutral'}
          />
        )}
      </div>
      <div className="pay-run-timeline" data-testid="pay-run-timeline">
        <PopoverGroupProvider>
          <ol className="pay-run-timeline__list">
            {timeline.steps.map((step, index) => (
              <TimelineStep
                key={step.id}
                step={step}
                isCurrent={timeline.currentStepId === step.id}
                isLast={index === timeline.steps.length - 1}
                busy={busy}
                onAction={(action) => onAction(action.verb)}
              />
            ))}
          </ol>
        </PopoverGroupProvider>
      </div>
    </section>
  );
};
